import React, { FC, useState } from "react";
import { Box, Chip, Divider, Menu, MenuItem, Tooltip } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";

export interface ProcessChipOption {
    key: string;
    label: string;
}

interface ProcessChipProps {
    /** Current process display name, e.g. "Selection 1". */
    label: string;
    /** Same-product processes this project can move to. */
    options: ProcessChipOption[];
    currentKey: string;
    onSelect: (key: string) => void;
    onNew: () => void;
    /** Locked (Pre-Production): render a static, non-editable chip. */
    readOnly?: boolean;
    /** False at one-selection-per-project capacity: hide "+ New process". */
    canCreateNew?: boolean;
    /** No compatible selection for this project's product — render a danger
        (TDC-red) chip prompting the user to pick or create one. */
    unassigned?: boolean;
}

// Compact, in-card control for a project's expert-selection process. It reads
// as a small tag but is the control itself: clicking opens a menu to reassign
// the project or start a new (same-product) process — keeping the choice inside
// the card instead of a full-width dropdown below it. Styled to sit alongside
// the portal's other small pills (see CHANNEL_PILL_SX / the "Just added" chip).
export const ProcessChip: FC<ProcessChipProps> = ({
    label,
    options,
    currentKey,
    onSelect,
    onNew,
    readOnly = false,
    canCreateNew = true,
    unassigned = false,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const close = () => setAnchorEl(null);
    // Danger (unassigned) styling wins over the default gold, but never over
    // the locked/read-only state.
    const danger = unassigned && !readOnly;
    // Precompute the palette + affordances per state so the JSX stays flat.
    // Both ramps stay literal on purpose: each is an interlocking 4-shade set
    // (rest / open / border / hover) and tokens exist only for the rest text +
    // fill, so a partial swap would put --accent-text on a literal open fill.
    const palette = danger
        ? {
              text: "#C0392B",
              icon: "#EB3E33",
              bg: "#FDECEA",
              bgOpen: "#FADBD8",
              border: "#EDA9A3",
              borderOpen: "#E38B83",
              hoverBg: "#FADBD8",
              hoverBorder: "#E38B83",
          }
        : {
              text: "#8a6e3f",
              icon: "#B08A4A",
              bg: "#F5EEDF",
              bgOpen: "#EFE4C9",
              border: "#E4D3AE",
              borderOpen: "#D6BE90",
              hoverBg: "#EFE4C9",
              hoverBorder: "#D6BE90",
          };
    let tooltipTitle = "Selection process";
    if (readOnly)
        tooltipTitle = "Locked — this selection has entered Pre-Production";
    else if (danger)
        tooltipTitle =
            "No selection for this product — pick one or create a new selection";
    let chipIcon = <WorkspacesOutlinedIcon sx={{ fontSize: 12 }} />;
    if (readOnly) chipIcon = <LockOutlinedIcon sx={{ fontSize: 12 }} />;
    else if (danger) chipIcon = <ErrorOutlineRoundedIcon sx={{ fontSize: 12 }} />;

    return (
        <>
            <Tooltip title={tooltipTitle} arrow enterDelay={400}>
                <Chip
                    size="small"
                    onClick={
                        readOnly
                            ? undefined
                            : (e) => setAnchorEl(e.currentTarget)
                    }
                    icon={chipIcon}
                    label={
                        <Box
                            component="span"
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "1px",
                                maxWidth: 128,
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {label}
                            </Box>
                            {!readOnly && (
                                <KeyboardArrowDownRoundedIcon
                                    sx={{
                                        fontSize: 14,
                                        mr: "-3px",
                                        flexShrink: 0,
                                        opacity: 0.75,
                                        transform: open
                                            ? "rotate(180deg)"
                                            : "none",
                                        transition: "transform .15s ease",
                                    }}
                                />
                            )}
                        </Box>
                    }
                    sx={{
                        height: 22,
                        borderRadius: "16px",
                        fontFamily: "CircularStd",
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: palette.text,
                        cursor: readOnly ? "default" : "pointer",
                        background: open ? palette.bgOpen : palette.bg,
                        border: `1px solid ${open ? palette.borderOpen : palette.border}`,
                        transition:
                            "background .15s ease, border-color .15s ease",
                        ...(readOnly
                            ? {}
                            : {
                                  "&:hover": {
                                      background: palette.hoverBg,
                                      borderColor: palette.hoverBorder,
                                  },
                              }),
                        // The ramps above are pale washes tuned for a white card;
                        // on the dark canvas they glare. Light is untouched — this
                        // only swaps the fill/border for the theme's own washes.
                        '[data-theme="dark"] &': {
                            background: danger ? "var(--down-bg)" : "var(--tint)",
                            color: danger ? "var(--down)" : "var(--accent-text)",
                            borderColor: "transparent",
                            "& .MuiChip-icon": {
                                color: danger ? "var(--down)" : "var(--accent-text)",
                            },
                            ...(readOnly
                                ? {}
                                : {
                                      "&:hover": {
                                          background: danger ? "var(--down-bg)" : "var(--tint)",
                                          borderColor: "var(--border)",
                                      },
                                  }),
                        },
                        "& .MuiChip-icon": {
                            color: palette.icon,
                            ml: "7px",
                            mr: "-3px",
                        },
                        "& .MuiChip-label": { px: "7px" },
                    }}
                />
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={close}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                    paper: {
                        elevation: 3,
                        sx: {
                            mt: "4px",
                            borderRadius: "10px",
                            border: "1px solid var(--border)",
                            minWidth: 176,
                            "& .MuiList-root": { py: "4px" },
                        },
                    },
                }}
            >
                {options.map((o) => (
                    <MenuItem
                        key={o.key}
                        selected={o.key === currentKey}
                        onClick={() => {
                            if (o.key !== currentKey) onSelect(o.key);
                            close();
                        }}
                        sx={{
                            fontFamily: "CircularStd",
                            fontSize: 12,
                            mx: "4px",
                            borderRadius: "7px",
                            "&.Mui-selected": {
                                background: "var(--tint)",
                                color: "var(--accent-text)",
                                fontWeight: 600,
                            },
                            "&.Mui-selected:hover": { background: "var(--tint)" },
                        }}
                    >
                        {o.label}
                    </MenuItem>
                ))}
                {canCreateNew && <Divider sx={{ my: "4px !important" }} />}
                {canCreateNew && (
                <MenuItem
                    onClick={() => {
                        onNew();
                        close();
                    }}
                    sx={{
                        fontFamily: "CircularStd",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--accent-text)",
                        mx: "4px",
                        borderRadius: "7px",
                        "&:hover": { background: "var(--tint)" },
                    }}
                >
                    + New process
                </MenuItem>
                )}
            </Menu>
        </>
    );
};
